import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Check, X, ChevronLeft, ChevronRight, Save, Loader2, GraduationCap } from 'lucide-react';

// Shared carousel attendance panel used by Lessons, Assessments, Skill Assessments & POE.
// State is owned by the parent and accessed via callbacks so each module can keep its own shape.
export const AttendancePanel = ({
    students,              // enrolled students (sorted, full list)
    groups = [],
    groupFilter = 'all',
    onGroupFilter,
    search = '',
    onSearch,
    predefinedComments = [],
    isPresent,             // (sid) => true | false | undefined
    onSetPresent,          // (sid, bool)
    predefinedFor,         // (sid) => string[]
    onTogglePredefined,    // (sid, label)
    commentFor,            // (sid) => string
    onSetComment,          // (sid, value)
    onSave,
    saving = false,
    saveTestId = 'save-attendance-btn',
}) => {
    const [idx, setIdx] = useState(0);

    const inGroup = (s) => groupFilter === 'all' || (groups.find(g => g.id === groupFilter)?.student_ids || []).includes(s.id);
    const matchesSearch = (s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.student_id || '').toLowerCase().includes(search.toLowerCase());
    const filtered = students.filter(s => inGroup(s) && matchesSearch(s));

    useEffect(() => { if (idx > filtered.length - 1) setIdx(0); }, [filtered.length, idx]);

    const current = filtered[Math.min(idx, Math.max(0, filtered.length - 1))];
    const presentCount = students.filter(s => isPresent(s.id) === true).length;

    if (students.length === 0) {
        return (
            <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 dark:text-slate-400">No students enrolled yet</p>
            </div>
        );
    }

    const pillsByType = (type) => predefinedComments.filter(pc => type === 'general' ? (!pc.type || pc.type === 'general') : pc.type === type);

    const renderPillGroup = (type, label, activeCls, idleCls) => {
        const list = pillsByType(type);
        if (list.length === 0) return null;
        return (
            <div className="mb-2">
                <span className={`text-xs font-medium mb-1 block ${type === 'merit' ? 'text-green-600' : type === 'demerit' ? 'text-red-600' : 'text-gray-500'}`}>{label}</span>
                <div className="flex gap-2 flex-wrap">
                    {list.map(pc => {
                        const active = (predefinedFor(current.id) || []).includes(pc.label);
                        return (
                            <button key={pc.id} onClick={() => onTogglePredefined(current.id, pc.label)}
                                data-testid={`att-pill-${pc.id}`}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${active ? activeCls : idleCls}`}>
                                {pc.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4" data-testid="attendance-tab">
            {/* Summary bar */}
            <div className="flex items-center gap-4">
                <div className="flex gap-3 flex-1">
                    <div className="bg-green-50 rounded-xl px-4 py-2 text-center min-w-[80px]">
                        <p className="text-xl font-bold text-green-700">{presentCount}</p>
                        <p className="text-xs text-green-600">Present</p>
                    </div>
                    <div className="bg-red-50 rounded-xl px-4 py-2 text-center min-w-[80px]">
                        <p className="text-xl font-bold text-red-700">{students.length - presentCount}</p>
                        <p className="text-xs text-red-600">Absent</p>
                    </div>
                    {onSearch && (
                        <Input value={search} onChange={e => { onSearch(e.target.value); setIdx(0); }}
                            placeholder="Search students..." className="max-w-[200px] h-10 text-sm" data-testid="att-search-input" />
                    )}
                </div>
                <Button onClick={onSave} disabled={saving} className="bg-ica-gold hover:bg-[#a8803a]" data-testid={saveTestId}>
                    {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Attendance</>}
                </Button>
            </div>

            {/* Group filter */}
            {groups.length > 0 && (
                <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => { onGroupFilter('all'); setIdx(0); }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${groupFilter === 'all' ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        All ({students.length})
                    </button>
                    {groups.map(g => (
                        <button key={g.id} onClick={() => { onGroupFilter(g.id); setIdx(0); }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${groupFilter === g.id ? 'bg-ica-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            data-testid={`group-filter-${g.id}`}>
                            {g.name} ({(g.student_ids || []).filter(sid => students.some(s => s.id === sid)).length})
                        </button>
                    ))}
                </div>
            )}

            {/* Progress bar */}
            <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="bg-ica-gold rounded-full h-2 transition-all" style={{ width: `${filtered.length > 0 ? ((idx + 1) / filtered.length) * 100 : 0}%` }} />
                </div>
                <span className="text-sm text-gray-500 font-medium min-w-[80px] text-right">{filtered.length > 0 ? `${idx + 1} of ${filtered.length}` : '0 of 0'}</span>
            </div>

            {/* Single student card */}
            {current && (
                <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden" data-testid="attendance-card">
                    <div className="p-6 space-y-5">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-ica-navy/10 rounded-full flex items-center justify-center">
                                <GraduationCap className="w-7 h-7 text-ica-navy dark:text-ica-light-gold" />
                            </div>
                            <div className="flex-1">
                                <p className="text-lg font-semibold text-gray-900 dark:text-slate-100">{current.name}</p>
                                <p className="text-sm text-gray-500 dark:text-slate-400">{current.student_id}</p>
                            </div>
                            {isPresent(current.id) !== undefined && (
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${isPresent(current.id) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {isPresent(current.id) ? 'PRESENT' : 'ABSENT'}
                                </span>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => onSetPresent(current.id, true)} data-testid={`att-present-${current.id}`}
                                className={`flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-semibold transition-all ${isPresent(current.id) === true ? 'bg-green-500 text-white shadow-lg shadow-green-200 scale-[1.02]' : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'}`}>
                                <Check className="w-5 h-5" /> Present
                            </button>
                            <button onClick={() => onSetPresent(current.id, false)} data-testid={`att-absent-${current.id}`}
                                className={`flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-semibold transition-all ${isPresent(current.id) === false ? 'bg-red-500 text-white shadow-lg shadow-red-200 scale-[1.02]' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'}`}>
                                <X className="w-5 h-5" /> Absent
                            </button>
                        </div>

                        {predefinedComments.length > 0 && (
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Comments</label>
                                {renderPillGroup('merit', 'Merits', 'bg-green-500 text-white shadow-sm', 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200')}
                                {renderPillGroup('demerit', 'Demerits', 'bg-red-500 text-white shadow-sm', 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200')}
                                {renderPillGroup('general', 'General', 'bg-ica-gold text-white shadow-sm', 'bg-gray-100 text-gray-600 hover:bg-gray-200')}
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Note</label>
                            <Input value={commentFor(current.id) || ''} onChange={e => onSetComment(current.id, e.target.value)}
                                placeholder="Add a note for this student..." className="text-sm" data-testid={`att-comment-${current.id}`} />
                        </div>
                    </div>

                    {/* Navigation footer */}
                    <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-slate-700/40 border-t border-gray-100 dark:border-slate-700">
                        <Button variant="outline" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="att-prev">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                        </Button>
                        <div className="flex gap-1 max-w-[300px] overflow-hidden">
                            {filtered.map((s, i) => (
                                <button key={s.id} onClick={() => setIdx(i)}
                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 transition-all ${i === idx ? 'bg-ica-gold scale-125' : isPresent(s.id) === true ? 'bg-green-400' : isPresent(s.id) === false ? 'bg-red-400' : 'bg-gray-300'}`} />
                            ))}
                        </div>
                        <Button variant="outline" onClick={() => setIdx(Math.min(filtered.length - 1, idx + 1))} disabled={idx >= filtered.length - 1} data-testid="att-next">
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendancePanel;
